import flatstore from "flatstore";
import React, { useEffect, useState, useLayoutEffect } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
// Import your actual components
import { isNil } from "lodash";
import Meta from "../../components/common/Meta";
import LoadModules from "../../components/modules/LoadModules";
import { useGlobalContext } from "../../init/GlobalContext";
import { getSubscriptionPaths, localStorageUtil } from "../../utils/functions";
import { getStaticPage } from "../../utils/services";
import CuratedSubscription from "./steps/CuratedSubscription";
import CustomizeSubscription from "./steps/CustomizeSubscription";
import SubscriptionPeriod from "./steps/SubscriptionPeriod";
import SubscriptionSize from "./steps/SubscriptionSize";
import GiftSelection from "./steps/GiftSelection";

const Subscription = ({ loading, subscription_static_data }) => {
  const [data, setData] = useState(null);
  const [modules, setModules] = useState(null);
  const [globalState, setGlobalState] = useGlobalContext();
  const { filter } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const paths = getSubscriptionPaths();
  const queryParams = new URLSearchParams(location.search);
  // Updated to handle trailing slash
  const parts = location.pathname.endsWith("/")
    ? location.pathname.slice(0, -1).split("/")
    : location.pathname.split("/");
  const latestPath = parts[parts.length - 1];
  const shouldReset = queryParams.get("reset") === "1";

  const matchedStep = Object.keys(paths.steps).find((step) =>
    paths.steps[step].endsWith(latestPath)
  );

  // Apply URL parameters synchronously BEFORE render to ensure presets show on first load
  useLayoutEffect(() => {
    if (!shouldReset) {
      return;
    }

    const type = queryParams.get("type");
    const size = queryParams.get("size");
    const period = queryParams.get("period");
    const nonce = queryParams.get("nonce");

    sessionStorage.setItem("resetting_subscription", "true");
    if (nonce) {
      sessionStorage.setItem("resetting_subscription_nonce", nonce);
    }

    // Parse the 'items' parameter (these are slugs that need to be matched with subscription data)
    const items = {};
    for (const [key, value] of queryParams.entries()) {
      if (key.startsWith("items[") && key.endsWith("]")) {
        const itemKey = key.slice(6, -1); // Extract the key inside the brackets
        items[itemKey] = value;
      }
    }

    // Apply all parameters synchronously in one update
    // prefilledItems is temporary - CustomizeSubscription will process it and convert to items, then delete it
    const subscriptionData = {};
    if (type) {
      subscriptionData.type = type;
    }
    if (size) {
      subscriptionData.size = size;
    }
    if (period) {
      subscriptionData.period = period;
    }
    if (type === "curated") {
      const curated = queryParams.get("curated");
      if (curated) {
        subscriptionData.curated = curated;
      }
    } else if (Object.keys(items).length > 0) {
      // Store temporarily - will be processed by CustomizeSubscription
      subscriptionData.prefilledItems = items;
    }

    // Apply all subscription data at once
    localStorageUtil.updateObjectItem("subscription", subscriptionData);
  }, [location.search, shouldReset]);

  useEffect(() => {
    subscription_static_data = null;
    setGlobalState({
      customeGlobalClasse: shouldReset ? ["subscription-reset"] : [],
    });
  }, []);

  useEffect(() => {
    if (isNil(subscription_static_data)) {
      getStaticPage("subscription", "subscription_static_data");
    }
    if (!data) {
      setData(subscription_static_data);
    }

    if (subscription_static_data && subscription_static_data.modules !== null) {
      setModules(subscription_static_data.modules);
    }
  }, [subscription_static_data]);

  // Dynamically select the component based on the last URL segment
  const renderComponentBasedOnPath = () => {
    switch (matchedStep) {
      case "period":
        return <SubscriptionPeriod />;
      case "gift":
        return <GiftSelection />;
      case "customize":
        return <CustomizeSubscription />;
      case "curated":
        return <CuratedSubscription />;
      default:
        return (
          <>
            {modules && (
              <LoadModules
                modules={modules}
                filter={filter}
                moduleData={null}
                type="top"
              />
            )}
            <SubscriptionSize isResetting={shouldReset} />
            {modules && (
              <LoadModules
                modules={modules}
                filter={filter}
                moduleData={null}
                type="bottom"
              />
            )}
          </>
        );
    }
  };

  return (
    <>
      {data && (
        <Meta
          title={data.seo_title ?? data.title}
          description={data.seo_description ?? data.body}
        />
      )}
      {renderComponentBasedOnPath()}
    </>
  );
};

export default flatstore.connect(["loading", "subscription_static_data"])(
  Subscription
);
